import { formatTime, type Time, time } from './time';
import type { OptionChoice, OptionDef, OptionStep } from './types';

const OPT_RE = /\{opt:([^}]+)\}/g;
const TIME_TOKEN_RE = /\{time\}/g;

export function defaultSelection(options: OptionDef[] | undefined): Record<string, string> {
  return Object.fromEntries((options ?? []).map((o) => [o.key, o.default]));
}

/** Sum of `addTime` across all of a choice's steps. */
export function choiceAddTime(choice: OptionChoice): Time {
  if (!choice.steps) return time();
  let total = 0;
  for (const step of Object.values(choice.steps)) total += step.addTime ?? 0;
  return time(0, total);
}

function expandStep(step: OptionStep, fallback: string | undefined): string | undefined {
  const raw = step.text ?? fallback;
  if (raw === undefined) return undefined;
  if (step.addTime === undefined) return raw;
  return raw.replace(TIME_TOKEN_RE, formatTime(step.addTime));
}

/**
 * Substitute `{opt:KEY:STEP}` placeholders with the selected choice's `steps[STEP]` expansion.
 * Throws on bare `{opt:KEY}` (no step), unknown option key, or missing step on the active choice.
 */
export function resolveOptionRefs(
  text: string,
  options: OptionDef[] | undefined,
  selection: Record<string, string>,
): string {
  if (!options?.length) return text;
  return text.replace(OPT_RE, (_, raw) => {
    const [optKeyRaw, stepRaw] = String(raw).split(':');
    const optKey = optKeyRaw?.trim() ?? '';
    const step = stepRaw?.trim();
    const opt = options.find((o) => o.key === optKey);
    if (!opt) throw new Error(`{opt:${optKey}} references unknown option`);
    if (!step) {
      throw new Error(`{opt:${optKey}} must specify a step (e.g. {opt:${optKey}:STEP})`);
    }
    const choiceKey = selection[opt.key] ?? opt.default;
    const choice = opt.choices.find((c) => c.key === choiceKey);
    const s = choice?.steps?.[step];
    if (!s) {
      throw new Error(
        `{opt:${optKey}:${step}} references missing step "${step}" on choice "${choiceKey}"`,
      );
    }
    const expanded = expandStep(s, choice?.label);
    if (expanded === undefined) {
      throw new Error(
        `{opt:${optKey}:${step}} on choice "${choiceKey}" has no \`text\` and no \`label\` to inherit from`,
      );
    }
    return expanded;
  });
}
